import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          /** `invisible` was removed from the Turnstile JS API; use `flexible` + `appearance`. */
          size?: 'normal' | 'compact' | 'flexible';
          /** `execute` keeps the widget hidden until `execute()` runs (on-demand token). */
          appearance?: 'always' | 'execute' | 'interaction-only';
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      /** With `appearance: 'execute'`, call this when you need a token (e.g. OTP confirm). */
      execute: (widgetIdOrContainer?: string | HTMLElement) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

/**
 * On-demand Cloudflare Turnstile (`appearance: 'execute'` + `executeTurnstile()`).
 * Do not mount the widget in `display:none`; it needs a real layout box (see `turnstileContainerClass`).
 */
export const TURNSTILE_CONTAINER_CLASS =
  'pointer-events-none fixed left-0 top-0 z-[-1] h-[70px] w-[min(100vw,300px)] opacity-0 overflow-hidden';
export function useTurnstile() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  /** Set by `executeTurnstile` while waiting for Cloudflare callback (or timeout). */
  const tokenPendingRef = useRef<((t: string | null) => void) | null>(null);

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return;

    const finishToken = (t: string | null) => {
      setToken(t);
      const pending = tokenPendingRef.current;
      tokenPendingRef.current = null;
      pending?.(t);
    };

    const tryRender = () => {
      if (!window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current !== null) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (t: string) => finishToken(t),
        'expired-callback': () => finishToken(null),
        'error-callback': () => {
          finishToken(null);
          const id = widgetIdRef.current;
          if (id && window.turnstile) {
            try {
              window.turnstile.reset(id);
            } catch {
              /* ignore */
            }
          }
        },
        size: 'flexible',
        appearance: 'execute',
        theme: 'auto',
      });
    };

    if (window.turnstile) {
      tryRender();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          tryRender();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  const resetTurnstile = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  /**
   * Resets the widget, runs the challenge, and resolves when a token is ready (or null on failure / timeout).
   */
  const executeTurnstile = useCallback((): Promise<string | null> => {
    if (!SITE_KEY || widgetIdRef.current === null || !window.turnstile?.execute) {
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      // If confirm was tapped twice quickly, a second execute() must not orphan the first waiter
      // (orphaned waiters used to let a code path continue without a matching Turnstile token).
      const staleResolve = tokenPendingRef.current;
      tokenPendingRef.current = null;
      staleResolve?.(null);

      const tid = window.setTimeout(() => {
        tokenPendingRef.current = null;
        resolve(null);
      }, 25_000);

      const done = (t: string | null) => {
        window.clearTimeout(tid);
        resolve(t);
      };

      tokenPendingRef.current = done;
      const widgetId = widgetIdRef.current!;
      try {
        window.turnstile!.reset(widgetId);
        // Let reset settle; immediate execute() can throw "already executing" (e.g. after 110200 retries).
        window.setTimeout(() => {
          if (tokenPendingRef.current !== done || widgetIdRef.current !== widgetId) return;
          try {
            window.turnstile!.execute(widgetId);
          } catch {
            window.clearTimeout(tid);
            tokenPendingRef.current = null;
            resolve(null);
          }
        }, 0);
      } catch {
        window.clearTimeout(tid);
        tokenPendingRef.current = null;
        resolve(null);
      }
    });
  }, []);

  return {
    token,
    resetTurnstile,
    executeTurnstile,
    turnstileRef: containerRef,
    turnstileEnabled: !!SITE_KEY,
  };
}
